import { z } from 'zod';

export const localeSchema = z.enum(['fr', 'en']);
export type Locale = z.infer<typeof localeSchema>;

export const localizedTextSchema = z.object({
  fr: z.string(),
  en: z.string(),
});
export type LocalizedText = z.infer<typeof localizedTextSchema>;

export const roleSchema = z.enum([
  'learner',
  'geek',
  'partner-staff',
  'juvo-staff',
  'admin',
]);
export type Role = z.infer<typeof roleSchema>;

export const trackSchema = z.enum([
  'digital-health',
  'ai',
  'technical',
  'health-tech',
  'digital-dentistry',
  'embedded',
]);
export type Track = z.infer<typeof trackSchema>;

export const courseLevelSchema = z.enum(['beginner', 'intermediate', 'advanced']);
export type CourseLevel = z.infer<typeof courseLevelSchema>;

export const programmeTypeSchema = z.enum(['workshop', 'certificate', 'professional']);
export type ProgrammeType = z.infer<typeof programmeTypeSchema>;

export const deliveryModeSchema = z.enum(['in-person', 'hybrid']);
export const certificateTypeSchema = z.enum(['juvo', 'joint']);
export const cohortStatusSchema = z.enum(['open', 'full', 'in-progress', 'completed', 'cancelled']);
export const enrollmentStatusSchema = z.enum([
  'waitlisted',
  'enrolled',
  'completed',
  'dropped',
]);
export const paymentMethodSchema = z.enum(['jvc', 'direct', 'subsidized']);
export const assetStatusSchema = z.enum([
  'intake',
  'diagnostics',
  'refurbishment',
  'qa',
  'storage',
  'deployed',
  'returned',
  'recycled',
]);
export const jvcTypeSchema = z.enum(['earn', 'redeem', 'adjustment']);

export const registerSchema = z.object({
  email: z.string().email(),
  name: z.string().min(2, 'Name must be at least 2 characters').max(120),
  phone: z.string().min(6).max(32).optional(),
  password: z
    .string()
    .min(6, 'Password must be at least 6 characters')
    .max(128)
    .optional()
    .or(z.literal('')),
  language: localeSchema.default('fr'),
});
export type RegisterInput = z.infer<typeof registerSchema>;

export const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const magicLinkSchema = z.object({
  email: z.string().email(),
});

export const catalogQuerySchema = z.object({
  q: z.string().optional(),
  track: trackSchema.optional(),
  level: courseLevelSchema.optional(),
  programmeType: programmeTypeSchema.optional(),
  locale: localeSchema.optional(),
});
export type CatalogQuery = z.infer<typeof catalogQuerySchema>;

export const enrollSchema = z.object({
  cohortId: z.string().min(1),
  paymentMethod: paymentMethodSchema.default('direct'),
});
export type EnrollInput = z.infer<typeof enrollSchema>;

export const contactSchema = z.object({
  email: z.string().email(),
});

export const publicUserSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  name: z.string(),
  phone: z.string().optional(),
  roles: z.array(roleSchema),
  language: localeSchema,
  jvcBalance: z.number(),
  location: z.string().optional(),
});
export type PublicUser = z.infer<typeof publicUserSchema>;

export const courseSchema = z.object({
  id: z.string(),
  slug: z.string(),
  code: z.string().optional(),
  heroImage: z.string().optional(),
  title: localizedTextSchema,
  description: localizedTextSchema,
  track: trackSchema,
  level: courseLevelSchema,
  programmeType: programmeTypeSchema,
  durationWeeks: z.number().int().positive(),
  deliveryMode: deliveryModeSchema,
  certificateType: certificateTypeSchema,
  published: z.boolean(),
  tags: z.array(z.string()),
  location: z.string(),
  upcoming: z
    .object({
      startDate: z.string(),
      endDate: z.string(),
      capacity: z.number().int().positive(),
      seatsTaken: z.number().int().nonnegative(),
      location: z.string(),
    })
    .optional(),
});
export type Course = z.infer<typeof courseSchema>;

export const createCourseSchema = z.object({
  slug: z.string().min(2),
  code: z.string().optional(),
  heroImage: z.string().optional(),
  title: localizedTextSchema,
  description: localizedTextSchema,
  track: trackSchema,
  level: courseLevelSchema,
  programmeType: programmeTypeSchema.default('workshop'),
  durationWeeks: z.number().int().positive().default(4),
  deliveryMode: deliveryModeSchema.default('in-person'),
  certificateType: certificateTypeSchema.default('juvo'),
  published: z.boolean().default(true),
  tags: z.array(z.string()).default([]),
  location: z.string().default('Faso Kanu, Bamako'),
});
export type CreateCourseInput = z.infer<typeof createCourseSchema>;

export const updateCourseSchema = createCourseSchema.partial();
export type UpdateCourseInput = z.infer<typeof updateCourseSchema>;

export const createCohortSchema = z.object({
  courseId: z.string(),
  courseSlug: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  capacity: z.number().int().positive().default(15),
  location: z.string().default('Faso Kanu, Bamako'),
  status: cohortStatusSchema.default('open'),
  language: localeSchema.default('fr'),
});
export type CreateCohortInput = z.infer<typeof createCohortSchema>;

export const cohortSchema = z.object({
  id: z.string(),
  courseId: z.string(),
  courseSlug: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  capacity: z.number().int().positive(),
  seatsTaken: z.number().int().nonnegative(),
  location: z.string(),
  status: cohortStatusSchema,
  language: localeSchema,
});
export type Cohort = z.infer<typeof cohortSchema>;

export const impactSnapshotSchema = z.object({
  period: z.string(),
  kgRefurbished: z.number(),
  cohortsRun: z.number(),
  partnersActive: z.number(),
  jvcIssued: z.number(),
  jvcRedeemed: z.number(),
  deployments: z.number(),
  peopleTrained: z.number(),
});
export type ImpactSnapshot = z.infer<typeof impactSnapshotSchema>;

export const updateImpactSchema = impactSnapshotSchema.partial();
export type UpdateImpactInput = z.infer<typeof updateImpactSchema>;

export const yearOneTargets = {
  cohortsRun: 50,
  kgRefurbished: 500,
  partnersActive: 20,
  deployments: 10,
} as const;

export const merchProductSchema = z.object({
  sku: z.string(),
  name: localizedTextSchema,
  description: localizedTextSchema,
  priceUsd: z.number().positive(),
  image: z.string(),
});
export type MerchProduct = z.infer<typeof merchProductSchema>;

export const updateMerchProductSchema = merchProductSchema.partial();
export type UpdateMerchProductInput = z.infer<typeof updateMerchProductSchema>;

export const merchOrderItemSchema = z.object({
  sku: z.string().min(1),
  qty: z.number().int().positive().max(20),
});

export const merchOrderSchema = z.object({
  name: z.string().min(2).max(120),
  email: z.string().email(),
  city: z.string().max(80).optional(),
  items: z.array(merchOrderItemSchema).min(1),
  note: z.string().max(500).optional(),
});
export type MerchOrderInput = z.infer<typeof merchOrderSchema>;

export const donationCampaignSchema = z.object({
  kgRefurbished: z.number(),
  devicesThisQuarter: z.number(),
  fundsRaisedUsd: z.number(),
  fundsTargetUsd: z.number(),
});
export type DonationCampaign = z.infer<typeof donationCampaignSchema>;

export const updateDonationCampaignSchema = donationCampaignSchema.partial();
export type UpdateDonationCampaignInput = z.infer<typeof updateDonationCampaignSchema>;

export const techDonationSchema = z.object({
  name: z.string().min(2).max(120),
  email: z.string().email(),
  phone: z.string().min(6).max(32).optional(),
  city: z.string().min(2).max(80),
  description: z.string().min(8).max(800),
  quantity: z.coerce.number().int().positive().max(200).default(1),
});
export type TechDonationInput = z.infer<typeof techDonationSchema>;

export const fundDonationSchema = z.object({
  name: z.string().min(2).max(120),
  email: z.string().email(),
  amountUsd: z.coerce.number().positive().max(100_000),
  note: z.string().max(500).optional(),
});
export type FundDonationInput = z.infer<typeof fundDonationSchema>;

export { catalogSeed, filterCatalog, impactSeed } from './seed/catalog.js';
export type { CatalogCourseSeed } from './seed/catalog.js';
export { merchSeed, donationCampaignSeed } from './seed/give.js';
